import React from 'react'
import { t } from '@lingui/core/macro'
import { createStyles, AppIcon } from '@codeleap/styles'
import { useMessageComposer, useStateStore } from 'stream-chat-react'
import type { MessageComposerState } from 'stream-chat'
import { ActionIcon, Icon, Text, View } from '@/components'
import { Chat } from '@/config/packages'
import {
  buildQuotedPreviewA11yLabel,
  ChatLocalMesage,
  getQuotedImageAlbumLabel,
  getQuotedPreview,
  QUOTED_PREVIEW_ICONS,
  QuotedPreviewModel,
} from '@packages/chat'
import { QuotedTextWithLinks } from '../../QuotedTextWithLinks'

type ResolvedPreview = Exclude<QuotedPreviewModel, { kind: 'deleted' }>

type MentionedUser = { id?: string; name?: string }

const PreviewContent = ({ preview, mentionedUsers }: { preview: ResolvedPreview; mentionedUsers?: MentionedUser[] }) => {
  const iconName = QUOTED_PREVIEW_ICONS[preview.kind as keyof typeof QUOTED_PREVIEW_ICONS] as AppIcon | undefined

  if (preview.kind === 'image') {
    const label = preview.count > 1
      ? getQuotedImageAlbumLabel(preview.count)
      : (preview.caption ?? t`Photo`)
    return (
      <View style={styles.row}>
        <Icon name='camera-01' style={styles.icon} />
        <QuotedTextWithLinks
          text={label}
          textCss={[styles.text, styles.rowText]}
          linkCss={styles.link}
          mentionCss={styles.mention}
          mentionedUsers={mentionedUsers}
          numberOfLines={1}
        />
      </View>
    )
  }
  if (preview.kind === 'gif') {
    return (
      <View style={styles.row}>
        <Icon name='sticker-circle' style={styles.icon} />
        <QuotedTextWithLinks
          text={preview.caption ?? t`GIF`}
          textCss={[styles.text, styles.rowText]}
          linkCss={styles.link}
          mentionCss={styles.mention}
          mentionedUsers={mentionedUsers}
          numberOfLines={1}
        />
      </View>
    )
  }
  if (preview.kind === 'text') {
    return (
      <QuotedTextWithLinks
        text={preview.text}
        textCss={[styles.text, styles.rowText]}
        linkCss={styles.link}
        mentionCss={styles.mention}
        mentionedUsers={mentionedUsers}
        numberOfLines={2}
      />
    )
  }

  const label =
    preview.kind === 'poll'
      ? preview.title
      : preview.kind === 'voice'
        ? t`Voice message (${preview.durationLabel})`
        : preview.filename

  return (
    <View style={styles.row}>
      {iconName ? <Icon name={iconName} style={styles.icon} /> : null}
      <Text style={[styles.text, styles.rowText]} text={label} />
    </View>
  )
}

const PreviewThumbnail = ({ preview }: { preview: ResolvedPreview }) => {
  if (preview.kind !== 'image' && preview.kind !== 'gif') return null
  if (!preview.thumbnailUrl) return null
  return <img src={preview.thumbnailUrl} alt='' css={styles.thumbnail} />
}

// Read the quoted message off the same Stream MessageComposer state mobile
// reads from (`ChatInputQuotedReplyBar`). Stream subscribes to
// `message.updated` internally and re-publishes the quoted message via this
// state when the source is edited (see stream-chat MessageComposer.ts —
// `subscribeMessageUpdated` → `setQuotedMessage(formatMessage(event.message))`),
// so the preview tracks edits live. The custom Chat.Store snapshot above
// the input only updated at click time, so it drifted away on every edit.
const composerQuotedSelector = (state: MessageComposerState) => ({
  quotedMessage: state.quotedMessage,
})

// @spec FR-0077/AC-1@v1 — composer preview bar above the input.
// @spec FR-0077/AC-4@v1 — dismiss returns to the default composer state.
// @spec FR-0077/AC-12@v1 — dismiss labelled "Cancel reply" for assistive tech.
export const QuotedComposerPreview = () => {
  const messageComposer = useMessageComposer()
  const { quotedMessage } = useStateStore(messageComposer.state, composerQuotedSelector)
  const preview = getQuotedPreview(quotedMessage as ChatLocalMesage | null)

  if (!quotedMessage || !preview || preview.kind === 'deleted') return null

  const senderLabel = preview.isCurrentUser ? t`You` : preview.senderName

  // Mirror the dismiss into the custom store so anything still reading
  // `Chat.Store.useQuotedMessage()` (the submit path, etc.) also clears.
  const handleDismiss = () => {
    Chat.Store.clearQuotedMessage()
    try {
      messageComposer?.setQuotedMessage?.(null)
    } catch {
      // Stream API change is harmless — the custom store is still cleared.
    }
  }

  return (
    <View
      style={styles.wrapper}
      role='region'
      aria-label={buildQuotedPreviewA11yLabel(preview)}
    >
      <View style={styles.accent} />
      <View style={styles.body}>
        <Text style={styles.sender} text={senderLabel} numberOfLines={1} />
        <PreviewContent preview={preview} mentionedUsers={(quotedMessage as ChatLocalMesage)?.mentioned_users as MentionedUser[] | undefined} />
      </View>
      <PreviewThumbnail preview={preview} />
      <ActionIcon
        name='x-circle'
        debugName='chat-input:dismiss-quick-reply'
        onPress={handleDismiss}
        aria-label={t`Cancel reply`}
        style={['iconSize:3', styles.dismiss]}
      />
    </View>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    ...theme.presets.row,
    alignItems: 'stretch',
    backgroundColor: theme.baseColors.primarySolid100,
    borderRadius: theme.radius.small,
    overflow: 'hidden',
    marginBottom: theme.spacing.value(1),
    minHeight: theme.spacing.value(6),
    gap: theme.spacing.value(1.5),
    paddingRight: theme.spacing.value(1.25),
    width: '100%',
    alignSelf: 'stretch',
  },
  accent: {
    width: 6,
    borderTopRightRadius: theme.radius.large,
    borderBottomRightRadius: theme.radius.large,
    backgroundColor: theme.baseColors.primarySolid500,
    flexShrink: 0,
  },
  body: {
    ...theme.presets.column,
    ...theme.presets.flex,
    minWidth: 0,
    paddingTop: theme.spacing.value(1),
    paddingBottom: theme.spacing.value(1),
    gap: theme.spacing.value(0.5),
    justifyContent: 'center',
  },
  sender: {
    color: theme.baseColors.primarySolid500,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '18px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  text: {
    color: theme.baseColors.onPrimarySolid50Heading,
    fontSize: 13,
    lineHeight: '18px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  link: {
    textDecoration: 'underline',
    color: theme.baseColors.primarySolid500,
  },
  mention: {
    fontWeight: 700,
    color: theme.baseColors.primarySolid500,
  },
  row: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    gap: theme.spacing.value(0.5),
    minWidth: 0,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  icon: {
    width: theme.spacing.value(1.5),
    height: theme.spacing.value(1.5),
    color: theme.baseColors.onPrimarySolid50Caption,
    flexShrink: 0,
  },
  thumbnail: {
    width: theme.spacing.value(5),
    height: theme.spacing.value(5),
    objectFit: 'cover',
    borderRadius: theme.radius.small,
    alignSelf: 'center',
    flexShrink: 0,
  },
  dismiss: {
    touchableWrapper: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      padding: 0,
      alignSelf: 'center',
      flexShrink: 0,
    },
    icon: {
      color: theme.baseColors.neutralSolid900,
    },
  },
}))
